import React, { useState, useRef, useEffect } from 'react';
import { DEFAULT_AVATAR, getDefaultAvatarWithInitial } from '../utils/defaultAvatar';
import { useFormattedTimestamp, useFormattedLastSeen } from '../utils/timestampFormatter';
import { connectSocket, getSocket, isSocketConnected } from '../socket';
import { DEFAULT_BUBBLE_COLOR, getBubbleHex, isValidBubbleColor, mixHex, rgba } from '../utils/bubbleColors';
import ProjectList from './ProjectList';
import './Sidebar.css';

// Legacy bucket markers (soft-block): treat these URLs as unavailable in UI.
const LEGACY_STORAGE_MARKERS = (() => {
  try {
    const raw = process.env.REACT_APP_LEGACY_STORAGE_MARKERS || '';
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  } catch {
    return [];
  }
})();
const isLegacyStorageUrl = (url) => {
  if (!LEGACY_STORAGE_MARKERS.length) return false;
  const s = String(url || '');
  return LEGACY_STORAGE_MARKERS.some((m) => s.includes(m));
};
const safeAvatarSrc = (src, username = null) => {
  if (!src || src === DEFAULT_AVATAR) return username ? getDefaultAvatarWithInitial(username) : DEFAULT_AVATAR;
  if (isLegacyStorageUrl(src)) return username ? getDefaultAvatarWithInitial(username) : DEFAULT_AVATAR;
  return src;
};

// Component for live updating timestamps
const TimestampDisplay = ({ timestamp }) => {
  const formattedTime = useFormattedTimestamp(timestamp);
  return <>{formattedTime}</>;
};

// Live "last seen" presence line for offline direct chats. Kept visually separate
// (own muted line, "last seen" prefix) from the room's last-message time above.
const LastSeenInline = ({ lastSeen, platform }) => {
  const label = useFormattedLastSeen(lastSeen);
  if (!label) return null;
  const device = platform === 'mobile' ? ' · Mob' : (platform === 'web' ? ' · PC' : '');
  return <div className="room-last-seen">{label}{device}</div>;
};

function Sidebar({
  user,
  rooms,
  friends,
  myBubbleColor,
  currentRoom,
  onSelectRoom,
  onStartChat,
  onCreateGroup,
  onDeleteGroup,
  onOpenSettings,
  onOpenFriendRequests,
  onOpenGroupSettings,
  onArchiveChat,
  isCollapsed = false,
  onToggleCollapsed,
  roomUnreadCounts = {},
  sidebarTab = 'chats',
  onSidebarTabChange,
  projects = [],
  currentProject,
  onSelectProject,
  onCreateProject,
  projectUnreadCounts = {}
}) {
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullStartYRef = useRef(null);
  const isPullingRef = useRef(false);
  const [chatSearch, setChatSearch] = useState('');
  const [contextMenu, setContextMenu] = useState(null); // { x, y, room }
  const longPressTimerRef = useRef(null);



  const handleCreateGroup = () => {
    if (groupName.trim() && selectedMembers.length > 0) {
      onCreateGroup(groupName, selectedMembers);
      setGroupName('');
      setSelectedMembers([]);
      setShowNewGroup(false);
    }
  };

  const toggleMemberSelection = (userId) => {
    setSelectedMembers((prev) => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const formatLastMessage = (message, room) => {
    if (!message) return 'No messages yet';
    
    // Determine sender prefix
    let senderPrefix = '';
    if (message.senderId === user.uid) {
      senderPrefix = 'You: ';
    } else if (room.type === 'group') {
      senderPrefix = `${message.senderName || 'Someone'}: `;
    }
    
    let content = '';
    if (message.type === 'text') {
      content = message.content.length > 30 
        ? message.content.substring(0, 30) + '...'
        : message.content;
    } else {
      const icons = {
        image: 'Photo',
        video: 'Video', 
        file: 'File'
      };
      content = icons[message.type] || 'Message';
    }
    
    return senderPrefix + content;
  };

  // Pull-to-refresh handlers for chat list
  const handleTouchStart = (e) => {
    const container = e.currentTarget;
    if (container.scrollTop <= 5) { // Allow small tolerance
      pullStartYRef.current = e.touches[0].clientY;
      isPullingRef.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!isPullingRef.current || pullStartYRef.current == null) return;
    const delta = e.touches[0].clientY - pullStartYRef.current;
    if (delta > 80 && !isRefreshing) {
      setIsRefreshing(true);
      refreshRoomsList();
      isPullingRef.current = false;
      pullStartYRef.current = null;
    }
  };

  const handleTouchEnd = () => {
    isPullingRef.current = false;
    pullStartYRef.current = null;
  };

  const refreshRoomsList = () => {
    (async () => {
      try {
        let socket = getSocket();
        if (!socket || !isSocketConnected()) {
          const token = await user.getIdToken();
          socket = connectSocket(token);
        }
        if (socket && isSocketConnected()) {
          console.log('📨 Refreshing rooms list');
          socket.emit('rooms:get');
          setTimeout(() => setIsRefreshing(false), 1500);
          return;
        }
      } catch (_) {}
      setIsRefreshing(false);
    })();
  };

  // Global click to close context menu
  useEffect(() => {
    const closeOnClick = () => setContextMenu(null);
    document.addEventListener('click', closeOnClick);
    return () => document.removeEventListener('click', closeOnClick);
  }, []);

  const requestDeleteChat = (room) => {
    if (!room) return;
    if (!window.confirm('Delete this chat for everyone?')) return;
    if (room.type === 'group') {
      onDeleteGroup(room.roomId);
    } else {
      const socket = getSocket();
      if (socket) {
        socket.emit('room:delete:direct', { roomId: room.roomId });
      }
    }
  };

  const handleRoomContextMenu = (e, room) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, room });
  };

  const handleRoomTouchStart = (e, room) => {
    // Start a long-press timer (600ms)
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      requestDeleteChat(room);
    }, 600);
  };

  const handleRoomTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const filteredFriends = friends.filter(f => {
    const q = chatSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (f.username || '').toLowerCase().includes(q) ||
      (f.email || '').toLowerCase().includes(q)
    );
  });

  const isMobile = () => window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

  const ringForKey = (key) => {
    const k = isValidBubbleColor(key) ? key : DEFAULT_BUBBLE_COLOR;
    return mixHex(getBubbleHex(k), '#1A1D21', 0.5);
  };

  const ringStyle = (key) => {
    const ring = ringForKey(key);
    return {
      border: `2px solid ${rgba(ring, 0.85)}`,
      boxShadow: '0 0 0 2px rgba(13, 15, 18, 0.9)'
    };
  };

  // When collapsed, close panels that need width.
  useEffect(() => {
    if (!isCollapsed) return;
    setShowNewChat(false);
    setShowNewGroup(false);
    setContextMenu(null);
  }, [isCollapsed]);

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="user-profile">
          <img 
            src={safeAvatarSrc(user.photoURL, user.displayName)}
            alt={user.displayName}
            className="profile-image"
            style={ringStyle(myBubbleColor)}
            loading="eager"
            decoding="async"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getDefaultAvatarWithInitial(user.displayName);
            }}
          />
          <div className="user-info">
            <h3>{user.displayName}</h3>
            <span className="status online">Online</span>
          </div>
        </div>
        <div className="header-actions">
          {/* Hide action buttons when collapsed (desktop) */}
          {!isCollapsed && (
            <button 
              onClick={onOpenFriendRequests}
              className="header-icon-btn"
              title="Add People"
            >
              <span className="material-symbols-outlined">person_add</span>
            </button>
          )}
          {!isCollapsed && (
            <button onClick={onOpenSettings} className="header-icon-btn" title="Settings">
              <span className="material-symbols-outlined">settings</span>
            </button>
          )}
          {/* Desktop-only collapse toggle (swapped after settings) */}
          {typeof onToggleCollapsed === 'function' && !isMobile() && (
            <button
              onClick={onToggleCollapsed}
              className="header-icon-btn sidebar-collapse-btn"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <span className="material-symbols-outlined">
                {isCollapsed ? 'chevron_right' : 'chevron_left'}
              </span>
            </button>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <div className="sidebar-tab-bar">
          <button
            className={`sidebar-tab ${sidebarTab === 'chats' ? 'active' : ''}`}
            onClick={() => onSidebarTabChange?.('chats')}
          >
            <span className="material-symbols-outlined">chat</span>
            <span>Chats</span>
            {Object.values(roomUnreadCounts).reduce((a, b) => a + b, 0) > 0 && sidebarTab !== 'chats' && (
              <span className="sidebar-tab-badge">
                {Object.values(roomUnreadCounts).reduce((a, b) => a + b, 0)}
              </span>
            )}
          </button>
          <button
            className={`sidebar-tab ${sidebarTab === 'projects' ? 'active' : ''}`}
            onClick={() => onSidebarTabChange?.('projects')}
          >
            <span className="material-symbols-outlined">folder_special</span>
            <span>Projects</span>
            {Object.values(projectUnreadCounts).reduce((a, b) => a + b, 0) > 0 && sidebarTab !== 'projects' && (
              <span className="sidebar-tab-badge">
                {Object.values(projectUnreadCounts).reduce((a, b) => a + b, 0)}
              </span>
            )}
          </button>
        </div>
      )}

      {!isCollapsed && sidebarTab === 'chats' && (
        <div className="sidebar-actions">
          <button
            onClick={() => setShowNewChat(!showNewChat)}
            className="action-btn"
            title="New Chat"
          >
            <span className="material-symbols-outlined">chat</span>
            <span className="action-label">New Chat</span>
          </button>
          <button
            onClick={() => setShowNewGroup(!showNewGroup)}
            className="action-btn"
            title="New Group"
          >
            <span className="material-symbols-outlined">group_add</span>
            <span className="action-label">New Group</span>
          </button>
        </div>
      )}


      {sidebarTab === 'chats' && showNewChat && (
        <div className="new-group-panel">
          <h4>Start a chat with a friend</h4>
          {friends.length === 0 ? (
            <div className="empty-friends">
              <p>No friends yet!</p>
              <p className="hint">Add some friends to start chatting</p>
            </div>
          ) : (
            <>
              <div className="search-bar" style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  placeholder="Search friends..."
                  value={chatSearch}
                  onChange={(e) => setChatSearch(e.target.value)}
                  className="group-name-input"
                />
              </div>
              <div className="users-list">
                {filteredFriends.map((friend) => (
                  <div 
                    key={friend.userId}
                    className="user-item selectable"
                    onClick={() => {
                      onStartChat(friend.userId);
                      setShowNewChat(false);
                      setChatSearch('');
                    }}
                  >
                    <img 
                      src={safeAvatarSrc(friend.photoURL, friend.username)}
                      alt={friend.username}
                      className="user-avatar"
                      style={ringStyle(friend.bubbleColor)}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getDefaultAvatarWithInitial(friend.username);
                      }}
                    />
                    <div className="user-details">
                      <span className="user-name">{friend.username}</span>
                      {friend.email && <span className="email">{friend.email}</span>}
                    </div>
                    {friend.online && <span className="material-symbols-outlined check-mark">bolt</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}


      {sidebarTab === 'chats' && showNewGroup && (
        <div className="new-group-panel">
          <h4>Create a group with friends</h4>
          <input
            type="text"
            placeholder="Group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className="group-name-input"
          />
          {friends.length === 0 ? (
            <div className="empty-friends">
              <p>No friends yet!</p>
              <p className="hint">Add some friends to create a group</p>
            </div>
          ) : (
            <>
              <div className="users-list">
                {friends.map((friend) => (
                  <div 
                    key={friend.userId}
                    className={`user-item selectable ${selectedMembers.includes(friend.userId) ? 'selected' : ''}`}
                    onClick={() => toggleMemberSelection(friend.userId)}
                  >
                    <img 
                      src={safeAvatarSrc(friend.photoURL, friend.username)}
                      alt={friend.username}
                      className="user-avatar"
                      style={ringStyle(friend.bubbleColor)}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getDefaultAvatarWithInitial(friend.username);
                      }}
                    />
                    <div className="user-details">
                      <span className="user-name">{friend.username}</span>
                    </div>
                    {selectedMembers.includes(friend.userId) && (
                      <span className="material-symbols-outlined check-mark">check_circle</span>
                    )}
                  </div>
                ))}
              </div>
              <button 
                onClick={handleCreateGroup}
                disabled={!groupName.trim() || selectedMembers.length === 0}
                className="create-group-btn"
              >
                Create Group
              </button>
            </>
          )}
        </div>
      )}

      {sidebarTab === 'projects' && (
        <ProjectList
          projects={projects}
          currentProject={currentProject}
          friends={friends}
          onSelectProject={onSelectProject}
          onCreateProject={onCreateProject}
          user={user}
          projectUnreadCounts={projectUnreadCounts}
        />
      )}

      {sidebarTab === 'chats' && <div
        className={`rooms-list ${isRefreshing ? 'refreshing' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <h4>Chats</h4>
        {isRefreshing && (
          <div className="refresh-indicator">Refreshing…</div>
        )}
        {rooms.filter(r => !r.archived).length === 0 ? (
          <div className="empty-state">
            <p>No conversations yet</p>
            <p className="hint">Add friends to start chatting</p>
          </div>
        ) : (
          rooms.filter(r => !r.archived).map((room) => (
            <div
              key={room.roomId}
              className={`room-item ${currentRoom?.roomId === room.roomId ? 'active' : ''}`}
              onClick={() => onSelectRoom(room)}
              onContextMenu={(e) => handleRoomContextMenu(e, room)}
              onTouchStart={(e) => handleRoomTouchStart(e, room)}
              onTouchEnd={handleRoomTouchEnd}
            >
              {roomUnreadCounts[room.roomId] && roomUnreadCounts[room.roomId] > 0 && (
                <div className="unread-indicator">
                  <span className="unread-count">{roomUnreadCounts[room.roomId]}</span>
                </div>
              )}
              <div
                className="room-avatar"
                style={(() => {
                  if (room.type === 'group') return undefined;
                  const otherUserId = room.members?.find(id => id !== user.uid);
                  const friend = friends.find(f => f.userId === otherUserId);
                  const ring = ringForKey(friend?.bubbleColor);
                  return { borderColor: rgba(ring, 0.6) };
                })()}
              >
                {room.type === 'group' ? (
                  <div className="dm-avatar-container">
                    {room.photoURL ? (
                      <img
                        src={safeAvatarSrc(room.photoURL, room.name)}
                        alt={room.name}
                        className="group-photo"
                        loading="eager"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = getDefaultAvatarWithInitial(room.name);
                        }}
                      />
                    ) : (
                      <span className="group-icon">G</span>
                    )}
                    {/* Group online indicator intentionally removed from the chat list. */}
                  </div>
                ) : (
                  <div className="dm-avatar-container">
                    {(() => {
                      const otherUserId = room.members?.find(id => id !== user.uid);
                      const friend = friends.find(f => f.userId === otherUserId);
                      return (
                    <img 
                      src={safeAvatarSrc(room.otherUserPhoto, room.name)}
                      alt={room.name}
                      className="direct-message-avatar"
                      style={ringStyle(friend?.bubbleColor)}
                      loading="eager"
                      decoding="async"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getDefaultAvatarWithInitial(room.name);
                      }}
                    />
                      );
                    })()}
                    {(() => {
                      const otherUserId = room.members?.find(id => id !== user.uid);
                      const friend = friends.find(f => f.userId === otherUserId);
                      if (!friend?.online) return null;
                      const platform = friend.platform || 'web';
                      const isMobile = platform === 'mobile';
                      return (
                        <div className="presence-indicator">
                          <div className="presence-dot-simple" />
                          <span className="presence-platform">{isMobile ? 'Mob' : 'PC'}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div className="room-details">
                <div className="room-header">
                  <span className="room-name">{room.name}</span>
                  <div className="room-actions">
                    {room.type === 'group' && (
                      <>
                        <button
                          className="group-settings-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenGroupSettings(room);
                          }}
                          title="Group settings"
                        >
                          <span className="material-symbols-outlined">settings</span>
                        </button>

                      </>
                    )}
                  </div>
                </div>
                <div className="room-last-message">
                  {formatLastMessage(room.lastMessage, room)}
                </div>
                {room.type === 'direct' && (() => {
                  const otherUserId = room.members?.find(id => id !== user.uid);
                  const friend = friends.find(f => f.userId === otherUserId);
                  if (!friend || friend.online || !friend.lastSeen) return null;
                  return (
                    <LastSeenInline
                      lastSeen={friend.lastSeen}
                      platform={friend.lastSeenPlatform}
                    />
                  );
                })()}
                {room.lastMessage && (
                  <div className="room-time">
                    <TimestampDisplay timestamp={room.lastMessage.timestamp} />
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>}

      {contextMenu && (
        <div 
          className="image-context-menu"
          style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, zIndex: 3000 }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.room.type === 'group' && (
            <button
              className="context-menu-item"
              onClick={() => {
                onOpenGroupSettings(contextMenu.room);
                setContextMenu(null);
              }}
            >
              <span className="material-symbols-outlined">settings</span>
              Group Settings
            </button>
          )}
          <button
            className="context-menu-item"
            onClick={() => {
              onArchiveChat?.(contextMenu.room);
              setContextMenu(null);
            }}
          >
            <span className="material-symbols-outlined">archive</span>
            Archive Chat
          </button>
          <button
            className="context-menu-item"
            onClick={() => {
              requestDeleteChat(contextMenu.room);
              setContextMenu(null);
            }}
            style={{ color: '#e11d48' }}
          >
            <span className="material-symbols-outlined">delete</span>
            Delete Chat
          </button>
        </div>
      )}
    </div>
  );
}

export default Sidebar;

